import React, {
  useState, useRef, useEffect, useCallback,
} from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useApolloClient } from '@apollo/client'
import { reverse } from 'lodash'
import { selectAuth } from '../../services/auth/selectors'
import { selectPrimaryLanguage } from '../../services/user/selectors'
import { setAiGa4Event } from '../../services/event-tracking/actions'
import { AI_RESPONSE_FEEDBACK_GA4_EVENT } from '../../services/event-tracking/index'
import { EN } from '../../services/languages/constants'
import { usePostHogEvent } from '../PostHog/index'
import { useTranslatedText } from '../OracleCommons/StaticTextContext'
import OracleChatInput from './OracleChatInput'

import { OracleUserMessage } from './OracleUserMessage'
import { OracleAgentMessage } from './OracleAgentMessage'
import { OracleChatMessagePairActions } from './OracleChatMessagePairActions'
import {
  useGetChatMessagesQuery,
  GetChatMessagesQuery,
  useUpdateMessagePairThumbsMutation,
  useGetChatSessionLazyQuery,
} from './queries.generated'
import { OracleThumbValue } from '../OracleCommons/OracleThumbs'
import { OracleModerationMessage, ModerationError, OracleLoadingSpinner } from '../OracleCommons'
import { OracleErrorToast } from '../OracleCommons/OracleErrorToast'
import { RateLimitState } from './rateLimitUtils'
import {
  SubscriptionDocuments,
  transformSubscriptionDocumentsForUI,
  transformCacheDocumentsForUI,
} from './hooks/useOracleChatSubscription'
import { isHiddenSyntheticMessage, getUserDisplayText } from './oracleSyntheticMessage'
import { ConversationPillButtons } from './ConversationPillButtons'
import { hasStreamingDivinationDocuments } from './hooks/hasStreamingDivinationDocuments'
import { useImagePreload } from './hooks/useImagePreload'
import { CategorizedTarotSeeds } from './hooks/seedQuestionTagUtils'
import { TarotNewReadingFAB } from './TarotNewReadingFAB'
import { useTarotTheaterContext } from './TarotTheater/TarotTheaterContext'
import { TarotTheater } from './TarotTheater/TarotTheater'
import { isTheaterActivePhase } from './TarotTheater/tarotTheaterMachine'

// Helper: extract terms from snippet's <mark> tags
const extractTermsFromSnippet = (html?: string): string[] => {
  if (!html) return []
  const matches = html.match(/<mark>(.*?)<\/mark>/g) || []
  return matches
    .map((m) => m.replace(/<\/?mark>/g, ''))
    .filter(Boolean)
}

// Helper: highlight ALL occurrences of each term
const highlightAllOccurrences = (el: HTMLElement, terms: string[]) => {
  // Check for numeric terms that might be in ordered lists
  // In ordered lists, the number is CSS-generated and not in text nodes
  const numericTerms = terms.filter((t) => /^\d+$/.test(t.trim()))
  if (numericTerms.length > 0) {
    // Find all ordered lists in the element
    const orderedLists = el.querySelectorAll('ol')
    orderedLists.forEach((ol) => {
      const listItems = ol.querySelectorAll('li')
      numericTerms.forEach((term) => {
        const listIndex = parseInt(term, 10) - 1 // Convert "1" to index 0
        if (listIndex >= 0 && listIndex < listItems.length) {
          const listItem = listItems[listIndex]
          // Add a highlight class to the list item
          listItem.classList.add('search-highlight-list-item')
        }
      })
    })
  }

  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT)
  const lowered = terms.map((t) => t.toLowerCase())
  const nodesToProcess: Node[] = []

  // Collect all text nodes first to avoid modifying during traversal
  let node: Node | null = walker.nextNode()
  while (node) {
    nodesToProcess.push(node)
    node = walker.nextNode()
  }

  // Process each text node
  nodesToProcess.forEach((textNode) => {
    const text = textNode.textContent || ''
    const lowerText = text.toLowerCase()
    const parent = textNode.parentNode
    if (!parent) return

    // Find all matches in this text node for all terms
    const matches: Array<{ start: number; end: number }> = []
    lowered.forEach((lowerTerm) => {
      let startPos = 0
      let matchPos = lowerText.indexOf(lowerTerm, startPos)
      while (matchPos >= 0) {
        matches.push({
          start: matchPos,
          end: matchPos + lowerTerm.length,
        })
        startPos = matchPos + 1
        matchPos = lowerText.indexOf(lowerTerm, startPos)
      }
    })

    if (matches.length === 0) return

    // Sort matches by position and remove overlaps
    matches.sort((a, b) => a.start - b.start)
    const nonOverlapping: typeof matches = []
    matches.forEach((match) => {
      if (nonOverlapping.length === 0
          || match.start >= nonOverlapping[nonOverlapping.length - 1].end) {
        nonOverlapping.push(match)
      }
    })

    // Build new DOM structure with highlights
    const fragment = document.createDocumentFragment()
    let lastPos = 0
    nonOverlapping.forEach((match) => {
      // Add text before match
      if (match.start > lastPos) {
        fragment.appendChild(document.createTextNode(text.slice(lastPos, match.start)))
      }
      // Add highlighted match
      const mark = document.createElement('mark')
      mark.className = 'search-highlight'
      mark.textContent = text.slice(match.start, match.end)
      fragment.appendChild(mark)
      lastPos = match.end
    })
    // Add remaining text
    if (lastPos < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastPos)))
    }

    // Replace the text node with the fragment
    parent.replaceChild(fragment, textNode)
  })
}

export const OracleExistingChat: React.FC<{
  currentChatSessionId: string,
  sendChat: (
    message: string,
    channelOverride?: string,
    personalityIdOverride?: string,
  ) => void,
  isOperationInProgress?: boolean,
  onStop?: () => void,
  pendingMessage?: string,
  streamingText?: string,
  streamChatSessionTitle?: string | null,
  streamingDocuments?: SubscriptionDocuments | null,
  onEnterVoiceMode?: () => void,
  personalityKey?: string,
  rateLimitState?: RateLimitState,
  scrollTargetPairId?: string,
  scrollTargetIndex?: number,
  onScrolledToTarget?: () => void,
  highlightSnippet?: string,
  highlightMatchSource?: string,
  isSearchActive?: boolean,
  moderationError?: ModerationError | null,
  chatChannel?: string | null,
  cardBackImageUrl?: string,
  streamCompletionCount?: number,
  isStreaming?: boolean,
  tarotCategorizedSeeds?: CategorizedTarotSeeds,
  isTarotCurrentMonth?: boolean,
  isReframeModalOpen?: boolean,
}> = ({
  currentChatSessionId,
  sendChat,
  isOperationInProgress = false,
  onStop,
  pendingMessage = '',
  streamingText = '',
  streamChatSessionTitle = null,
  streamingDocuments = null,
  onEnterVoiceMode,
  personalityKey,
  rateLimitState,
  scrollTargetPairId,
  scrollTargetIndex,
  onScrolledToTarget,
  highlightSnippet,
  highlightMatchSource,
  isSearchActive,
  moderationError,
  chatChannel = null,
  cardBackImageUrl,
  streamCompletionCount = 0,
  isStreaming = false,
  tarotCategorizedSeeds,
  isTarotCurrentMonth = true,
  isReframeModalOpen = false,
}) => {
  const auth = useSelector(selectAuth)
  const language = useSelector(selectPrimaryLanguage) || EN
  const apolloClient = useApolloClient()
  const normalizedPersonalityKey = personalityKey?.toLowerCase() || 'sage'
  const personalityDisplayName = useTranslatedText({ keys: ['pillSelector', normalizedPersonalityKey] })
  const placeholderText = useTranslatedText({
    keys: ['chatInput', 'placeholderExistingChat'],
    params: personalityKey ? { personality: personalityDisplayName } : {},
  })
  const errorLabel = useTranslatedText({ keys: ['errors', 'error'] })

  const isTarotChannel = chatChannel === 'tarot'

  // Network-warm the card back image for the tarot theater. The persistent
  // cards inside TarotCardStage own the real reveal gate — they decode the
  // rendered <img> — because img.decode() on a throwaway Image() instance does
  // not reliably warm the decoded-pixel cache for separately created <img> tags.
  useImagePreload(cardBackImageUrl, isTarotChannel)

  // Error toast state
  const [showErrorToast, setShowErrorToast] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>()

  // Error message translations
  const messagesLoadFailedError = useTranslatedText({ keys: ['errors', 'messagesLoadFailed'] })
  const thumbsUpdateFailedError = useTranslatedText({ keys: ['errors', 'thumbsUpdateFailed'] })
  const sessionLoadFailedError = useTranslatedText({ keys: ['errors', 'sessionLoadFailed'] })

  const conversationRef = useRef<HTMLDivElement>(null)
  const dispatch = useDispatch()

  const {
    data,
    error,
    loading,
    fetchMore,
  } = useGetChatMessagesQuery({
    variables: {
      chatSessionId: currentChatSessionId,
      // Load only most recent messages - this means we do not initially get the
      // entire history if it is a long conversation. Scroll up to fetch.
      limit: 10,
      offset: 0,
      order: [['createdAt', 'DESC']], // Get newest messages first
      language,
    },
    context: {
      jwt: auth.get('jwt'),
      useMaster: true,
    },
    fetchPolicy: 'cache-and-network',
    skip: !currentChatSessionId, // Skip for new sessions without ID
  })

  // Handle chat messages query error with toast
  React.useEffect(() => {
    if (error && !data) {
      setErrorMessage(messagesLoadFailedError)
      setShowErrorToast(true)
    }
  }, [error, data, messagesLoadFailedError])

  // Query to poll for chat session name updates (only if not using stream title)
  const [getChatSession] = useGetChatSessionLazyQuery({
    fetchPolicy: 'network-only', // Always fetch fresh data when polling
  })

  const [updateThumbs] = useUpdateMessagePairThumbsMutation()

  const trackMessageFeedback = usePostHogEvent('ai_message_feedback')
  const [thumbsValues, setThumbsValues] = useState<Record<string, OracleThumbValue>>({})
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Scroll indicator state
  const [showScrollIndicator, setShowScrollIndicator] = useState(false)

  // Simple filler management - keep filler until next message starts
  const [showFiller, setShowFiller] = useState(false)
  const [fillerHeight, setFillerHeight] = useState(0) // Will be calculated on mount

  // Track previous scroll position for filler shrinking
  const prevScrollTop = useRef(0)

  // Flag to prevent filler shrinking during message positioning
  const [isPositioningMessage, setIsPositioningMessage] = useState(false)

  // Track if we've done the initial scroll to bottom
  const hasInitiallyScrolled = useRef(false)

  // Calculate dynamic filler height to allow user bubble to be at top
  const calculateFullFillerHeight = useCallback(() => Math.max(window.innerHeight - 200, 37), [])

  // Function to fetch more messages when scrolling to top
  const fetchOlderMessages = useCallback(async () => {
    if (!currentChatSessionId) {
      return
    }

    const currentMessages = data?.oracleMessagePairList?.oracleMessagePairs || []
    const limit = 10

    // Only fetch more if we have at least the limit number of messages
    if (currentMessages.length < limit) {
      return
    }

    const currentOffset = currentMessages.length

    // Store scroll position before fetching to prevent jump
    const container = conversationRef.current
    const scrollTop = container?.scrollTop || 0
    const scrollHeight = container?.scrollHeight || 0

    try {
      await fetchMore({
        variables: {
          offset: currentOffset,
          limit,
          order: [['createdAt', 'DESC']], // Match initial query ordering
          language,
        },
        context: {
          jwt: auth.get('jwt'),
          useMaster: true,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev

          const newMessages = fetchMoreResult.oracleMessagePairList.oracleMessagePairs
          const existingMessages = prev.oracleMessagePairList.oracleMessagePairs

          // With DESC ordering, append older messages to existing messages
          const combinedMessages = [...existingMessages, ...newMessages]
          const uniqueMessages = combinedMessages.filter(
            (msg, index, arr) => arr.findIndex((m) => m.id === msg.id) === index,
          )

          return {
            ...prev,
            oracleMessagePairList: {
              ...prev.oracleMessagePairList,
              oracleMessagePairs: uniqueMessages,
            },
          }
        },
      })

      // Restore scroll position after new content loads to prevent jump
      // BUT skip if we're scrolling to a specific target (let that handle scroll)
      if (!scrollTargetPairId) {
        requestAnimationFrame(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight
            const heightDifference = newScrollHeight - scrollHeight
            container.scrollTop = scrollTop + heightDifference
          }
        })
      }
    } catch {
      // Silently handle fetch errors - not critical if pagination fails
    }
  }, [
    fetchMore,
    auth,
    currentChatSessionId,
    data?.oracleMessagePairList?.oracleMessagePairs,
  ])

  const updateThumb = async (messagePairId: string, newValue: OracleThumbValue) => {
    const pair = data?.oracleMessagePairList?.oracleMessagePairs.find((p) => p.id === messagePairId)
    const backendValue = (pair?.thumbsValue as OracleThumbValue) || 'none'
    const localValue = thumbsValues[messagePairId]
    const currentValue = localValue !== undefined ? localValue : backendValue

    if (currentValue === 'none' && newValue === 'none') {
      return
    }

    let valueToSend: OracleThumbValue
    if (currentValue === newValue) {
      valueToSend = 'none'
      setThumbsValues((prev) => ({
        ...prev,
        [messagePairId]: 'none',
      }))
    } else {
      valueToSend = newValue
      setThumbsValues((prev) => ({
        ...prev,
        [messagePairId]: newValue,
      }))
    }

    try {
      await updateThumbs({
        variables: {
          id: messagePairId,
          input: { thumbsValue: valueToSend },
        },
        context: {
          jwt: auth.get('jwt'),
          useMaster: true,
        },
      })

      // GA4 AI event
      if (valueToSend !== 'none') {
        dispatch(
          setAiGa4Event(
            AI_RESPONSE_FEEDBACK_GA4_EVENT.mergeDeep({
              content_rating: valueToSend,
            }),
          ),
        )
      }

      if (valueToSend !== 'none') {
        trackMessageFeedback(valueToSend === 'up' ? 'Up' : 'Down')
      }
    } catch {
      setErrorMessage(thumbsUpdateFailedError)
      setShowErrorToast(true)
      // Silently handle errors - show error toast to user
    }
  }
  // Update scroll indicator visibility based on content position
  const updateScrollIndicator = useCallback(() => {
    if (conversationRef.current) {
      const container = conversationRef.current

      // Calculate actual content height (excluding filler whitespace)
      let contentHeight = container.scrollHeight
      let currentFillerHeight = 0
      if (showFiller) {
        const fillerElement = container.querySelector('.oracle-existing-chat__response-filler')
        if (fillerElement) {
          currentFillerHeight = fillerElement.clientHeight
          contentHeight -= currentFillerHeight
        }
      }

      // Show indicator only if real content is scrolled 40px+ below visible area
      const scrolledFromBottom = contentHeight - container.scrollTop - container.clientHeight
      setShowScrollIndicator(scrolledFromBottom > 40)
    }
  }, [showFiller])

  // Handle user scroll interaction
  const handleScroll = useCallback(() => {
    if (conversationRef.current) {
      const container = conversationRef.current
      const currentScrollTop = container.scrollTop
      const isAtTop = currentScrollTop <= 10 // 10px threshold

      // Fetch more messages when scrolling to top
      if (isAtTop) {
        fetchOlderMessages()
      }

      // Shrink filler on downward scroll (but not during message positioning)
      if (showFiller && !isPositioningMessage && currentScrollTop > prevScrollTop.current) {
        const scrollDelta = currentScrollTop - prevScrollTop.current
        setFillerHeight((prevHeight) => Math.max(prevHeight - scrollDelta, 37))
      }

      // Update previous scroll position
      prevScrollTop.current = currentScrollTop

      // Update scroll indicator
      updateScrollIndicator()
    }
  }, [fetchOlderMessages, updateScrollIndicator, showFiller, isPositioningMessage])

  // Scroll to bottom with smooth animation
  const scrollToBottom = useCallback(() => {
    if (conversationRef.current) {
      const container = conversationRef.current

      // Get the conversation div within the scroll container
      const conversationDiv = container.querySelector('.oracle-existing-chat__conversation')
      if (!conversationDiv) return

      // Get all direct children of the conversation container
      const children = Array.from(conversationDiv.children)

      // Filter out the response filler
      const contentChildren = children.filter((child) => !child.classList.contains('oracle-existing-chat__response-filler'))

      // Get the last content element
      const lastContentElement = contentChildren[contentChildren.length - 1]

      if (lastContentElement) {
        // Calculate scroll to show the bottom of the last content element
        const elementRect = lastContentElement.getBoundingClientRect()
        const containerRect = container.getBoundingClientRect()
        const currentScrollTop = container.scrollTop
        const targetScrollTop = currentScrollTop + (elementRect.bottom - containerRect.bottom) + 20
        const top = Math.max(0, targetScrollTop)
        container.scrollTo({
          top,
          behavior: 'smooth',
        })
      } else {
        // Fallback to original behavior if no content found
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth',
        })
      }
    }
  }, [])

  // Scroll to bottom instantly (no animation) - used for initial load
  const scrollToBottomInstant = useCallback(() => {
    if (conversationRef.current) {
      const container = conversationRef.current

      // Get the conversation div within the scroll container
      const conversationDiv = container.querySelector('.oracle-existing-chat__conversation')
      if (!conversationDiv) return

      // Get all direct children of the conversation container
      const children = Array.from(conversationDiv.children)

      // Filter out the response filler
      const contentChildren = children.filter((child) => !child.classList.contains('oracle-existing-chat__response-filler'))

      // Get the last content element
      const lastContentElement = contentChildren[contentChildren.length - 1]

      if (lastContentElement) {
        // Calculate scroll to show the bottom of the last content element
        const elementRect = lastContentElement.getBoundingClientRect()
        const containerRect = container.getBoundingClientRect()
        const currentScrollTop = container.scrollTop
        const targetScrollTop = currentScrollTop + (elementRect.bottom - containerRect.bottom) + 20
        const top = Math.max(0, targetScrollTop)
        container.scrollTop = top // Direct assignment for instant scroll
      } else {
        // Fallback to original behavior if no content found
        container.scrollTop = container.scrollHeight // Direct assignment for instant scroll
      }
    }
  }, [])

  // Scroll user message to 16px from top after sending
  const scrollUserMessageToTop = useCallback(() => {
    if (conversationRef.current) {
      const container = conversationRef.current

      // Set flag to prevent filler shrinking during positioning
      setIsPositioningMessage(true)

      // Find the last user message element
      const userMessages = container.querySelectorAll('.oracle-user-message')
      const lastUserMessage = userMessages[userMessages.length - 1] as HTMLElement

      if (lastUserMessage) {
        // Calculate scroll position to place user message 16px from top
        const containerRect = container.getBoundingClientRect()
        const messageRect = lastUserMessage.getBoundingClientRect()
        const currentScrollTop = container.scrollTop
        const targetScrollTop = currentScrollTop + (messageRect.top - containerRect.top) - 16
        const top = Math.max(0, targetScrollTop)
        container.scrollTo({
          top,
          behavior: 'smooth',
        })

        // Clear positioning flag after scroll completes
        setTimeout(() => {
          setIsPositioningMessage(false)
        }, 1000) // Allow time for smooth scroll to complete
      } else {
        setIsPositioningMessage(false)
      }
    }
  }, [])

  // Add scroll event listener
  useEffect(() => {
    const container = conversationRef.current
    if (container) {
      container.addEventListener('scroll', handleScroll)
      return () => container.removeEventListener('scroll', handleScroll)
    }
    return undefined
  }, [handleScroll])

  // Update scroll indicator when messages change or component mounts
  useEffect(() => {
    // Use setTimeout to ensure DOM has updated after message changes
    const timeoutId = setTimeout(() => {
      updateScrollIndicator()
    }, 50)

    return () => clearTimeout(timeoutId)
  }, [
    data?.oracleMessagePairList?.oracleMessagePairs,
    pendingMessage,
    streamingText,
    showFiller,
    updateScrollIndicator,
  ])

  // Poll for chat session name updates after 1st and 3rd message pairs
  useEffect(() => {
    // Skip polling if we have a stream title
    if (streamChatSessionTitle) {
      return
    }

    if (!data?.oracleMessagePairList) return

    const messageCount = data.oracleMessagePairList.oracleMessagePairs.length

    // Query for name updates after 1st or 3rd message pair
    if (messageCount === 1 || messageCount === 3) {
      // Wait 5 seconds then query once for name updates
      timeoutRef.current = setTimeout(async () => {
        try {
          await getChatSession({
            variables: { chatSessionId: currentChatSessionId },
            context: {
              jwt: auth.get('jwt'),
              useMaster: true,
            },
          })

          // Note: We don't need to refetch here anymore as the sidebar will handle
          // its own smart refetch when needed. The cache update should be sufficient
          // to show any name changes in the sidebar.
        } catch {
          setErrorMessage(sessionLoadFailedError)
          setShowErrorToast(true)
          // Silently handle errors - not critical if name update fails
        }
      }, 5000) // Wait 5 seconds
    }
  }, [data?.oracleMessagePairList, getChatSession, currentChatSessionId, streamChatSessionTitle])

  // Cleanup the title-update timeout on component unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [])

  // Scroll to bottom on initial load when messages are first loaded
  useEffect(() => {
    if (data?.oracleMessagePairList?.oracleMessagePairs
        && data.oracleMessagePairList.oracleMessagePairs.length > 0
        && !hasInitiallyScrolled.current
        && !loading
        && !scrollTargetPairId) {
      // Use requestAnimationFrame to ensure DOM has rendered the messages
      requestAnimationFrame(() => {
        scrollToBottomInstant()
        hasInitiallyScrolled.current = true
      })
    }
  }, [
    data?.oracleMessagePairList?.oracleMessagePairs,
    loading,
    scrollToBottomInstant,
    scrollTargetPairId,
  ])

  // Reset initial scroll flag when switching to a different chat session
  useEffect(() => {
    hasInitiallyScrolled.current = false
  }, [currentChatSessionId])

  // Track previous pending message to detect new message starts
  const prevPendingMessage = useRef(pendingMessage)

  // Set initial filler height on mount
  useEffect(() => {
    setFillerHeight(calculateFullFillerHeight())
  }, [calculateFullFillerHeight])

  // Manage filler lifecycle - show when message starts, persist until next message starts
  useEffect(() => {
    const isNewMessage = pendingMessage && pendingMessage !== prevPendingMessage.current

    if (isNewMessage && isOperationInProgress) {
      // Hide any existing filler from previous message, then show new filler
      setShowFiller(false)

      // Show new filler in next tick to ensure clean transition
      setTimeout(() => {
        const newFillerHeight = calculateFullFillerHeight()
        setShowFiller(true)
        setFillerHeight(newFillerHeight) // Reset to large height for new response

        // Wait for DOM update, then scroll to position user message correctly
        requestAnimationFrame(() => {
          setTimeout(() => {
            scrollUserMessageToTop()
          }, 100) // Small delay to ensure DOM is updated
        })
      }, 0)
    }

    // Update previous pending message
    prevPendingMessage.current = pendingMessage
  }, [pendingMessage, isOperationInProgress, scrollUserMessageToTop, calculateFullFillerHeight])

  // Update Apollo cache with stream chat session title when available
  useEffect(() => {
    if (streamChatSessionTitle && currentChatSessionId) {
      // Update the cache with the stream title
      apolloClient.cache.modify({
        id: apolloClient.cache.identify({ __typename: 'OracleChatSession', id: currentChatSessionId }),
        fields: {
          title: () => streamChatSessionTitle,
        },
      })

      // Note: The sidebar will handle its own refetch when needed
      // No need to refetch here as the cache update should be sufficient
    }
  }, [streamChatSessionTitle, currentChatSessionId, apolloClient])

  // Use Apollo data directly - it handles pagination automatically
  const backendMessages = data?.oracleMessagePairList?.oracleMessagePairs || []

  // Messages are in DESC order (newest to oldest) - reverse using lodash to get ASC order
  // Since we eliminated streamMessagePairs, we only use Apollo cache data
  const orderedByCreatedAtAscMessages = reverse([...backendMessages])

  // Auto-scroll to a specific message pair when temporal URL params are provided
  // IMPORTANT: This hook must be before any early returns to maintain hook order
  useEffect(() => {
    if (!scrollTargetPairId || typeof scrollTargetIndex !== 'number' || !conversationRef.current) {
      return
    }

    // CRITICAL FIX: Don't try to scroll if we don't have data yet
    // This prevents scrolling to bottom when switching conversations
    if (!data?.oracleMessagePairList?.oracleMessagePairs?.length) {
      return
    }

    const container = conversationRef.current

    const tryScrollToElement = () => {
      const el = container.querySelector(`[data-message-pair-id="${scrollTargetPairId}"]`) as HTMLElement | null
      if (el) {
        // CRITICAL: Set flag BEFORE scrolling to prevent other scroll effects from interfering
        hasInitiallyScrolled.current = true

        const containerRect = container.getBoundingClientRect()
        const elRect = el.getBoundingClientRect()
        const top = container.scrollTop + (elRect.top - containerRect.top) - 16
        const targetScrollTop = Math.max(0, top)

        // Use instant scroll to avoid race conditions with smooth scroll
        container.scrollTop = targetScrollTop

        // Verify scroll happened
        requestAnimationFrame(() => {
          if (Math.abs(container.scrollTop - targetScrollTop) > 10) {
            container.scrollTop = targetScrollTop
          }
        })

        // Apply highlighting (persists until conversation change)
        const terms = extractTermsFromSnippet(highlightSnippet)
        if (terms.length) {
          // First, clear ALL previous highlights in the entire conversation
          container.querySelectorAll('mark.search-highlight').forEach((mark) => {
            const parent = mark.parentNode
            if (!parent) return
            while (mark.firstChild) {
              parent.insertBefore(mark.firstChild, mark)
            }
            parent.removeChild(mark)
          })

          // Find which specific message (user or agent) to highlight based on matchSource
          const userMessageEl = el.querySelector('.oracle-user-message') as HTMLElement | null
          const agentMessageEl = el.querySelector('.oracle-chatbot-message') as HTMLElement | null

          // Determine target message based on matchSource field from backend
          let targetMessageEl: HTMLElement | null = null

          // Be flexible with matchSource format
          const sourceValue = (highlightMatchSource || '').toLowerCase()

          if (sourceValue.includes('user')) {
            targetMessageEl = userMessageEl
          } else if (sourceValue.includes('oracle') || sourceValue.includes('agent')) {
            targetMessageEl = agentMessageEl
          } else {
            // Fallback: try to find which message contains the terms
            if (userMessageEl) {
              const userText = userMessageEl.textContent?.toLowerCase() || ''
              const hasMatch = terms.some((term) => userText.includes(term.toLowerCase()))
              if (hasMatch) {
                targetMessageEl = userMessageEl
              }
            }
            if (!targetMessageEl && agentMessageEl) {
              const agentText = agentMessageEl.textContent?.toLowerCase() || ''
              const hasMatch = terms.some((term) => agentText.includes(term.toLowerCase()))
              if (hasMatch) {
                targetMessageEl = agentMessageEl
              }
            }
          }

          // Apply highlighting only to the matched message
          if (targetMessageEl) {
            highlightAllOccurrences(targetMessageEl, terms)
          }
        }

        if (onScrolledToTarget) onScrolledToTarget()
        return true
      }
      return false
    }

    // Try scrolling immediately with a small delay to ensure DOM is ready
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (tryScrollToElement()) {
          return
        }

        // If element not found, try with retries
        let retries = 0
        const maxRetries = 10
        const retryInterval = 100

        const retryScroll = () => {
          if (tryScrollToElement()) {
            return
          }
          retries += 1
          if (retries < maxRetries) {
            setTimeout(retryScroll, retryInterval)
          } else {
            // Element still not found, try fetching more data
            const limit = 10
            const offset = Math.floor(scrollTargetIndex / limit) * limit
            fetchMore({
              variables: { offset, limit, order: [['createdAt', 'DESC']] },
              context: { jwt: auth.get('jwt'), useMaster: true },
              updateQuery: (prev, { fetchMoreResult }) => fetchMoreResult || prev,
            }).then(() => {
              // Wait for DOM to update and scroll restoration to complete
              // Use multiple requestAnimationFrames to ensure everything is ready
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  setTimeout(() => {
                    tryScrollToElement()
                  }, 150)
                })
              })
            }).catch(() => {
              // Best-effort only
            })
          }
        }

        setTimeout(retryScroll, retryInterval)
      })
    })
  }, [
    scrollTargetPairId,
    scrollTargetIndex,
    conversationRef,
    fetchMore,
    auth,
    data?.oracleMessagePairList?.oracleMessagePairs,
    onScrolledToTarget,
    highlightSnippet,
    highlightMatchSource,
  ])

  // Clear highlights when switching to a different conversation or starting a new chat
  useEffect(() => {
    const container = conversationRef.current
    if (!container) return

    // Remove all search highlights when conversation changes
    container.querySelectorAll('mark.search-highlight').forEach((mark) => {
      const parent = mark.parentNode
      if (!parent) return
      while (mark.firstChild) {
        parent.insertBefore(mark.firstChild, mark)
      }
      parent.removeChild(mark)
    })
  }, [currentChatSessionId])

  // Clear highlights when user exits search mode
  useEffect(() => {
    const container = conversationRef.current
    if (!container) return

    // If not in search mode, clear all highlights
    if (!isSearchActive && container.querySelector('mark.search-highlight')) {
      container.querySelectorAll('mark.search-highlight').forEach((mark) => {
        const parent = mark.parentNode
        if (!parent) return
        while (mark.firstChild) {
          parent.insertBefore(mark.firstChild, mark)
        }
        parent.removeChild(mark)
      })
    }
  }, [isSearchActive])

  // Tarot shuffle shows until the first card is drawn (TarotDocument in messages).
  // Unlike Oracle (where oracle_begin has an empty response), tarot_begin returns
  // a real greeting, so we can't use hasOnlyEmptyOracleBeginPairs.
  const hasTarotCard = backendMessages.some(
    (pair) => pair.documents?.some((doc) => doc.tarot),
  )
  // Suppress the greeting/animation while the reframe modal is open so the
  // two surfaces stay mutually exclusive (AI-764 follow-up fix).
  const showTarotGreeting = chatChannel === 'tarot'
    && !hasTarotCard
    && !isReframeModalOpen

  // --- Tarot Theater context ---------------------------------------------------
  const theater = useTarotTheaterContext()
  // useTarotTheater returns a fresh object each render, so `theater` itself is
  // an unstable dependency. Depend on the primitive phase and the
  // useCallback-stable handlers instead, so the effects below only re-run on
  // meaningful change rather than every render.
  const theaterPhase = theater?.phase
  const enterNewTarot = theater?.enterNewTarot
  const enterExistingTarot = theater?.enterExistingTarot
  const clarifyingCard = theater?.clarifyingCard
  const reset = theater?.reset

  // Clarifier pill ("Pull a clarifying card") → tarot_idle → pick_a_card,
  // skipping the shuffle stage. The pill itself still sends the
  // tarot_clarifier_pull chat action (handled inside ConversationPillButtons);
  // this only drives the theater.
  const handleClarifierPull = useCallback(() => {
    if (!clarifyingCard) return
    clarifyingCard()
  }, [clarifyingCard])

  // Scroll effect keyed on the FSM `flip` phase (the fly-and-flip state): bring
  // the landing spot into view so the card doesn't fly off-screen on mobile.
  useEffect(() => {
    if (theaterPhase !== 'flip') return
    const container = conversationRef.current
    if (!container) return
    const landings = container.querySelectorAll('[data-oracle-card-landing]')
    const landing = landings.length > 0 ? landings[landings.length - 1] : null
    if (!landing) return
    const containerRect = container.getBoundingClientRect()
    const landingRect = landing.getBoundingClientRect()
    const scrollOffset = container.scrollTop
      + (landingRect.top - containerRect.top) - 16
    container.scrollTo({
      top: Math.max(0, scrollOffset),
      behavior: 'smooth',
    })
  }, [theaterPhase])

  // Boot routing. Both entry paths must wait for the messages query to
  // resolve: before it does, `backendMessages` is empty, so `hasTarotCard` is
  // spuriously false and `showTarotGreeting` spuriously true. Firing
  // `enterNewTarot` during that window mis-routes an existing (already-drawn)
  // session into a `shuffle` that has no live stream to resolve it — the FSM
  // gets stuck there, and the later `enterExistingTarot` is a no-op because
  // ENTER_EXISTING_TAROT is idle-only. Gating on `!loading && data` removes the
  // race; after load, `showTarotGreeting` and `hasTarotCard` are mutually
  // exclusive (the former is defined as `... && !hasTarotCard`), so exactly one
  // path fires.
  const prevShowTarotGreetingRef = useRef(false)
  const hasBootedExistingRef = useRef(false)
  const messagesLoaded = !loading && !!data
  // The Tarot Theater FSM lives in a provider mounted ABOVE this component and is
  // shared across thread switches (it only self-resets on active true->false,
  // which never happens switching between two tarot threads). Without an explicit
  // reset, the newly-selected thread inherits the previous thread's FSM phase;
  // because ENTER_EXISTING_TAROT is idle-only, boot routing can't recover a
  // non-idle leftover phase, so isAnimatingTarotCard stays true and the reading is
  // CSS-hidden (blank content area). On a genuine session change, reset the FSM to
  // idle and clear the boot-routing refs so the new thread re-boots cleanly.
  const prevSessionIdRef = useRef(currentChatSessionId)
  useEffect(() => {
    if (prevSessionIdRef.current === currentChatSessionId) return
    prevSessionIdRef.current = currentChatSessionId
    prevShowTarotGreetingRef.current = false
    hasBootedExistingRef.current = false
    if (reset) reset()
  }, [currentChatSessionId, reset])
  useEffect(() => {
    if (!enterNewTarot) return
    // Do NOT evaluate (or seed) the greeting edge until the query resolves, so
    // the spurious pre-load `showTarotGreeting=true` is never read as a rising
    // edge and the real post-load edge for a genuine new reading still fires.
    if (!messagesLoaded) return
    // Rising edge of showTarotGreeting → new tarot session starting.
    if (showTarotGreeting && !prevShowTarotGreetingRef.current) {
      enterNewTarot()
    }
    prevShowTarotGreetingRef.current = showTarotGreeting
  }, [enterNewTarot, showTarotGreeting, messagesLoaded, currentChatSessionId])

  useEffect(() => {
    if (!enterExistingTarot) return
    if (hasBootedExistingRef.current) return
    if (!messagesLoaded) return
    // Mount into an existing session that already has a card drawn.
    if (chatChannel === 'tarot' && hasTarotCard) {
      hasBootedExistingRef.current = true
      // TODO(AI-882): a session with an accepted question but no card yet would
      // want enterExistingNoQuestion; we have no clean discriminator at this
      // scope, so a drawn card routes to tarot_idle (correct) and the no-card
      // case falls through to enterNewTarot above.
      enterExistingTarot()
    }
  }, [enterExistingTarot, chatChannel, hasTarotCard, messagesLoaded, currentChatSessionId])

  // Only show loading/error states if we don't have streaming text
  // This must come AFTER all hooks
  if (!streamingText && !isOperationInProgress) {
    if (loading && !data) {
      return null
    }
    if (error && !data) {
      return <p>{errorLabel}</p>
    }
    if (!data) {
      return null
    }
  }

  // Keep the theater mounted during animation even after the TarotDocument lands
  // in the cache. We keep it mounted for ALL non-idle tarot phases — including the
  // resting `tarot_idle` — so it survives the land → tarot_idle edge. That lets
  // the theater run its post-land reveal overlap (holding the flip overlay opaque
  // while the structured card fades in beneath it; AI-823). At rest in tarot_idle
  // it renders null, so keeping it mounted is harmless.
  const showTarotGreetingOrAnimating = showTarotGreeting
    || (chatChannel === 'tarot' && !!theater && theater.phase !== 'idle')

  const hasStreamingTarotDocs = streamingDocuments?.some((d) => d.tarot) ?? false
  // AI-882 issue 1: hide the underlying chat content (structured tarot reading,
  // title/date, user bubble) for the whole lifetime of a tarot reading EXCEPT
  // its revealed resting state, `tarot_idle`. Keying only off the FSM phase is
  // insufficient: on a new reading the streamed tarot doc renders before
  // `enterNewTarot` fires shuffle (the stream outpaces the messages query, whose
  // load gates boot routing), so there's an `idle` window where the doc paints
  // and the answer leaks via the 300ms fade-in. So we also hide whenever a
  // reading is in flight — streaming in (hasStreamingTarotDocs && isStreaming)
  // or already committed (hasTarotCard) — until we reach the `tarot_idle` reveal.
  const hasTarotReadingInFlight = (hasStreamingTarotDocs && isStreaming) || hasTarotCard
  const isAnimatingTarotCard = chatChannel === 'tarot'
    && !!theater
    && theater.phase !== 'tarot_idle'
    && (isTheaterActivePhase(theater.phase) || hasTarotReadingInFlight)
  const hasDivinationDocs = hasStreamingDivinationDocuments(streamingDocuments)

  const existingChatClass = [
    'oracle-existing-chat',
    isAnimatingTarotCard ? 'oracle-existing-chat--animating-card' : '',
  ].filter(Boolean).join(' ')

  return (
    <div className={existingChatClass}>
      {isTarotChannel && (
        <TarotNewReadingFAB
          seed={tarotCategorizedSeeds?.start ?? null}
          sendChat={sendChat}
          isOperationInProgress={isOperationInProgress}
          isCurrentMonth={isTarotCurrentMonth}
        />
      )}
      <div className="oracle-existing-chat__scroll_container" ref={conversationRef}>
        <div className="oracle-existing-chat__conversation">
          {showTarotGreetingOrAnimating && theater && (
            <TarotTheater
              phase={theater.phase}
              frontImageUrl={theater.frontImageUrl}
              shuffleElapsedMs={theater.shuffleElapsedMs}
              backImageUrl={cardBackImageUrl}
              contentAreaRef={conversationRef}
              onChooseCard={theater.chooseCard}
              onFlipEnd={theater.flipEnd}
            />
          )}
          {orderedByCreatedAtAscMessages.map((pair) => (
            <ChatPair
              key={pair.id}
              pair={pair}
              updateThumb={updateThumb}
              thumbsValues={thumbsValues}
              language={language}
            />
          ))}

          {/* Show pending message while operation is in progress */}
          {isOperationInProgress && pendingMessage && (
            <div className="oracle-existing-chat__pending-message">
              {!isHiddenSyntheticMessage(pendingMessage) && (
                <OracleUserMessage
                  text={getUserDisplayText(pendingMessage)}
                />
              )}
              <div className="oracle-chatbot-message">
                {(streamingText || hasDivinationDocs) && (
                  <>
                    <OracleAgentMessage
                      text={streamingText}
                      documents={transformSubscriptionDocumentsForUI(streamingDocuments, language)}
                      isStreaming
                    />
                    <OracleChatMessagePairActions
                      className="oracle-hidden"
                      thumbsValue={'none'} />
                  </>
                )}
                {!streamingText && !hasDivinationDocs && (
                  <OracleLoadingSpinner />
                )}
              </div>
            </div>
          )}

          {/* Show moderation error with user message */}
          {moderationError && pendingMessage && !isOperationInProgress && (
            <div className="oracle-existing-chat__pending-message">
              <OracleUserMessage text={pendingMessage} />
              <OracleModerationMessage
                severity={moderationError.config.severity}
                showCrisisResources={moderationError.config.showCrisisResources}
              />
            </div>
          )}

          {/* Stable whitespace filler - keeps layout stable */}
          {showFiller && (
            <div
              className="oracle-existing-chat__response-filler"
              style={{
                height: `${fillerHeight}px`,
              }}
            />
          )}

          {/* Mentioned content is forthcoming but not for current release.
          <OracleMentionedContent items={mockMentionedContentData} /> */}

        </div>
      </div>

      <div className="oracle-existing-chat__input-wrapper">
        <div className="oracle-existing-chat__input">
          <ConversationPillButtons
            chatChannel={chatChannel}
            chatSessionId={currentChatSessionId}
            sendChat={sendChat}
            isOperationInProgress={isOperationInProgress}
            streamCompletionCount={streamCompletionCount}
            onClarifierPull={handleClarifierPull}
            tarotCategorizedSeeds={tarotCategorizedSeeds}
          />
          <OracleChatInput
          sendChat={sendChat}
          placeholder={placeholderText}
          isOperationInProgress={isOperationInProgress}
          onStop={onStop}
          pendingMessage={pendingMessage}
          onEnterVoiceMode={onEnterVoiceMode}
          rateLimitState={rateLimitState}
          showScrollIndicator={showScrollIndicator}
          onScrollToBottom={scrollToBottom}
          showPills={false}
          showDisclaimer={true} />
        </div>
      </div>

      <OracleErrorToast
        isVisible={showErrorToast}
        onClose={() => {
          setShowErrorToast(false)
          setErrorMessage(undefined)
        }}
        message={errorMessage}
      />
    </div>
  )
}

const ChatPair: React.FC<{
  pair: GetChatMessagesQuery['oracleMessagePairList']['oracleMessagePairs'][number]
  updateThumb: (messagePairId: string, newValue: OracleThumbValue) => void
  thumbsValues: Record<string, OracleThumbValue>
  language: string
}> = ({
  pair,
  updateThumb,
  thumbsValues,
  language,
}) => {
  const thumbsValue = thumbsValues[pair.id] || (pair.thumbsValue as OracleThumbValue) || 'none'

  // Transform cache documents to UI format
  const documents = transformCacheDocumentsForUI(pair.documents, language)

  // Hide synthetic messages (e.g., oracle_begin) that have no display text
  const hideUserMessage = isHiddenSyntheticMessage(pair.userMessage.content)
  const displayText = getUserDisplayText(pair.userMessage.content)

  return (
    <div data-message-pair-id={pair.id}>
      {!hideUserMessage && (
        <OracleUserMessage
          text={displayText}
          mode={pair.mode as 'text' | 'voice'}
        />
      )}
      <OracleAgentMessage text={pair.oracleMessage.content} mode={pair.mode as 'text' | 'voice'} documents={documents} createdAt={pair.createdAt}/>
      <OracleChatMessagePairActions
        thumbsValue={thumbsValue}
        onThumbsDown={() => updateThumb(pair.id, 'down')}
        onThumbsUp={() => updateThumb(pair.id, 'up')}
      />
    </div>
  )
}
